import React, { ComponentPropsWithoutRef, ReactNode, useState } from "react";
import { twMerge } from "tailwind-merge";

import { getCharactersCount } from "~/common/utils";
import ClosedEyeIcon from "../icons/ClosedEyeIcon";
import OpenedEyeIcon from "../icons/OpenedEyeIcon";
import { ErrorText } from "./typography";

interface InputProps extends ComponentPropsWithoutRef<"input"> {
  label?: string;
  placeholder?: string;
  error?: string;
  className?: string;
  children?: ReactNode;
  tooltip?: ReactNode;
  defaultValue?: string;
  isError?: boolean;
  inputClassName?: string;
  description?: ReactNode;
  containerClassName?: string;
  charactersCount?: number;
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      id,
      label,
      error,
      type = "text",
      placeholder,
      className,
      defaultValue,
      children,
      isError = false,
      inputClassName,
      description,
      containerClassName,
      charactersCount = 0,
      maxLength,
      disabled,
      tooltip,
      ...props
    }: InputProps,
    ref,
  ) => {
    const [showPassword, setShowPassword] = useState(false);
    const passwordVisible = showPassword ? "text" : "password";
    const isCurrency = type === "currency";
    const showCharactersCount = disabled
      ? false
      : !!error || (!!maxLength && type === "text");

    return (
      <div
        className={twMerge(
          "flex w-full flex-col gap-2 font-light text-gray-700",
          containerClassName,
        )}
      >
        {(label ?? description) && (
          <div className="flex w-full flex-col">
            <div className="flex items-center justify-start gap-1">
              {label && (
                <div className="flex items-center">
                  <label
                    className="flex justify-between text-sm font-medium"
                    htmlFor={id}
                  >
                    <span className="first-letter:capitalize">{label}</span>
                  </label>
                </div>
              )}
              {tooltip && tooltip}
            </div>
            {description && <p className="text-sm">{description}</p>}
          </div>
        )}
        <div
          className={twMerge(
            "flex w-full items-center justify-between rounded-md border border-gray-300 bg-neutral-25 text-sm",
            disabled && "bg-gray-200",
            className,
            (isError || error) && "border-error bg-red-50",
          )}
        >
          {isCurrency && (
            <span className="px-3 leading-normal text-gray-500">$</span>
          )}
          <input
            className={twMerge(
              "h-11 w-full rounded-md border-0 bg-transparent text-sm placeholder:text-left focus:outline-none focus:ring-0 disabled:bg-gray-200 disabled:text-gray-700",
              (isError || error) && "text-error",
              inputClassName,
              isCurrency && "pl-0",
            )}
            id={id}
            placeholder={placeholder ?? `Enter ${label?.toLocaleLowerCase()}`}
            {...props}
            disabled={disabled}
            ref={ref}
            type={type === "password" ? passwordVisible : type}
            defaultValue={defaultValue}
          />
          {children}
          {type === "password" && (
            <button
              className="px-4 focus:outline-none focus:ring-transparent"
              type="button"
              onClick={() => setShowPassword(!showPassword)}
            >
              {showPassword ? (
                <OpenedEyeIcon className={error && "text-error"} />
              ) : (
                <ClosedEyeIcon className={error && "text-error"} />
              )}
            </button>
          )}
        </div>
        {showCharactersCount && (
          <div
            className={twMerge("flex justify-between", !error && "justify-end")}
          >
            {error?.trim() && <ErrorText>{error}</ErrorText>}
            {maxLength && type === "text" && (
              <span className="mr-1 self-end text-xs">
                {getCharactersCount(charactersCount, maxLength)}
              </span>
            )}
          </div>
        )}
      </div>
    );
  },
);

Input.displayName = "Input";
export default Input;
